import { Football, GoogleLogo, ArrowRight, Lightning, Binoculars, ChartLineUp, Target, Check, NumberOne, NumberTwo, NumberThree, NumberFour, NumberFive } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { trackEvent } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";

const steps = [
  {
    num: NumberOne,
    title: "CONNECT YOUR SLEEPER ACCOUNT",
    desc: "Enter your Sleeper username — no password needed. We pull your league data directly from Sleeper's API in seconds. See all your dynasty leagues instantly.",
    detail: "Works with any Sleeper dynasty league: Superflex, PPR, TE Premium, 1QB — all scoring formats supported.",
    color: "#ccff00",
  },
  {
    num: NumberTwo,
    title: "SEE REAL-TIME PLAYER VALUES",
    desc: "Every player and draft pick on your roster gets a live dynasty trade value from FantasyCalc. Values are adjusted for your league's specific scoring settings.",
    detail: "6pt passing TD? TE Premium? We detect it from your league settings and adjust values automatically — no manual configuration.",
    color: "#4ade80",
  },
  {
    num: NumberThree,
    title: "GET AI TRADE SUGGESTIONS",
    desc: "Select a player or pick you want to trade, and our AI analyzes every team in your league to find balanced, realistic trade packages.",
    detail: "Each suggestion includes value balance, waiver adjustment for uneven trades, positional fit analysis, and why the other team would accept.",
    color: "#45b7d1",
  },
  {
    num: NumberFour,
    title: "SIMULATE BEFORE YOU TRADE",
    desc: "See exactly how a trade impacts your roster before proposing it. Before/after power rankings, positional grades, and total value changes.",
    detail: "No more guessing. Know whether a trade makes you better or worse before you pull the trigger.",
    color: "#f9ca24",
  },
  {
    num: NumberFive,
    title: "DOMINATE YOUR LEAGUE",
    desc: "Use the Trade Finder to auto-scan for opportunities, Mock Draft to prepare for rookies, and Contender Rankings to target the right trade partners.",
    detail: "Save trades to your Inbox, share proposals via link, and track which ones you executed. Your dynasty command center.",
    color: "#ff6b6b",
  },
];

export default function HowItWorks() {
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = '/api/auth/google/login';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO page="how-it-works" />
      {/* Header */}
      <PublicNav />

      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white uppercase mb-4" data-testid="hiw-title">
            HOW <span className="text-[#ccff00]">IT WORKS</span>
          </h1>
          <p className="text-lg text-[#a3a3a3] max-w-2xl mx-auto">
            From connecting your league to executing winning trades — here's how Dynasty Trade Maker transforms your dynasty experience in 5 simple steps.
          </p>
        </div>

        {/* Video */}
        <div className="mb-20 border border-[#262626] rounded-sm overflow-hidden bg-black max-w-3xl mx-auto">
          <video controls preload="metadata" className="w-full h-auto" poster="/hero-images/about-hero.jpg" data-testid="hiw-video">
            <source src="/video/how-it-works.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Steps */}
        <div className="space-y-16">
          {steps.map((step, idx) => {
            const NumIcon = step.num;
            return (
              <div key={idx} className={`flex flex-col ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-start gap-8`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-sm" style={{ backgroundColor: `${step.color}15`, border: `1px solid ${step.color}30` }}>
                      <NumIcon size={22} weight="bold" style={{ color: step.color }} />
                    </div>
                    <h2 className="font-heading text-xl sm:text-2xl text-white uppercase tracking-tight">{step.title}</h2>
                  </div>
                  <p className="text-base text-[#a3a3a3] leading-relaxed mb-3">{step.desc}</p>
                  <p className="text-sm text-[#666] leading-relaxed">{step.detail}</p>
                </div>
                <div className="flex-1 w-full border border-[#262626] bg-[#171717] p-6 rounded-sm">
                  <div className="h-32 flex items-center justify-center">
                    <NumIcon size={64} weight="thin" style={{ color: `${step.color}30` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-20 py-12 border-t border-[#262626]">
          <h2 className="font-heading text-3xl text-white uppercase mb-3">READY TO GET STARTED?</h2>
          <p className="text-sm text-[#a3a3a3] mb-6">Import your Sleeper leagues in seconds. No credit card required.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => { trackEvent("try_free_click"); window.location.href = "/"; }} className="h-11 px-8 btn-primary rounded-sm text-sm">
              <Lightning size={16} weight="bold" className="mr-2" /> TRY FREE — NO SIGN UP
            </Button>
            <Button onClick={handleGoogleLogin} className="h-11 px-8 bg-white text-black font-bold text-sm rounded-sm hover:bg-gray-100">
              GO PRO — FROM $19.99/yr
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
